import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import { useNotification } from 'hooks/useNotification';
import {
  CreateBakIntegrationsErrorResponse,
  CreateBakIntegrationsPayload,
  CreateBakIntegrationsSuccessResponse,
  createBakIntegrationsApi,
} from 'IntegrationModule/api/createBakIntegrationsApi';
import { integrationQueryKeys } from './integrationQueryKeys';

/**
 * Mutation hook for POST /bak-integrations.
 *
 * The bak-integrations controller swallows BadRequestExceptions in its catch
 * block and returns `{ status: 400, message }` with HTTP 200, so we inspect
 * the body status code to surface failures. Throwing here ensures
 * `mutateAsync` rejects and any `Promise.all` orchestration fails fast.
 */
export const useProvisionBakIntegrationMutation = () => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<CreateBakIntegrationsSuccessResponse>,
    | Error
    | AxiosResponse<CreateBakIntegrationsErrorResponse>
    | { message?: string; data?: { message?: string } },
    CreateBakIntegrationsPayload
  >({
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      const response = (await createBakIntegrationsApi({
        params,
        token,
      })) as AxiosResponse<
        CreateBakIntegrationsSuccessResponse | CreateBakIntegrationsErrorResponse
      >;

      const softErrorStatus =
        (response.data as CreateBakIntegrationsErrorResponse | undefined)?.statusCode ??
        (response.data as CreateBakIntegrationsSuccessResponse | undefined)?.status ??
        0;
      if (softErrorStatus >= 400) {
        throw new Error((response.data as { message?: string })?.message || 'Unknown error');
      }

      return response as AxiosResponse<CreateBakIntegrationsSuccessResponse>;
    },
    onSuccess: () => {
      showSuccess('Bak integration provisioned');
      queryClient.invalidateQueries({ queryKey: integrationQueryKeys.lists() });
    },
    onError: (error) => {
      // `handleError` in src/services/api.ts spreads Error instances into plain
      // objects, so `instanceof Error` is unreliable here. Probe the common
      // shapes (ApiError body, top-level message) before falling back.
      const errorObj = error as
        | {
            data?: { message?: string };
            message?: string;
          }
        | null
        | undefined;
      const message = errorObj?.data?.message || errorObj?.message || 'Unknown error';
      showError(`Failed to provision bak integration: ${message}`);
    },
  });

  return {
    provisionBakIntegration: mutation.mutate,
    provisionBakIntegrationAsync: mutation.mutateAsync,
    isProvisioningBakIntegration: mutation.isPending,
  };
};
